import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import ProfileSettingsForm from '../../components/account/ProfileSettingsForm';
import SubscriptionPanel from '../../components/account/SubscriptionPanel';
import PLAN_CATALOG from '../../constants/plans';
import {
  getPublishedPlans,
  subscribeToPublishedPlans,
} from '../../modules/publicPlanStorage';
import { consumeSelectedPlanId } from '../../modules/planSelectionStorage';
import { accountApi } from '../../modules/api';
import { formatMonthlyPrice } from '../../utils/pricing';
import {
  mapSubscriptionFromApi,
  mapSubscriptionsFromApi,
} from '../../utils/subscription';
import { setSession } from '../../modules/auth';

import './account.css';

const EMPTY_PROFILE = {
  displayName: '',
  email: '',
  jobTitle: '',
  bio: '',
  location: '',
};

const mapUserToProfile = (user) => ({
  displayName: (user && (user.name || user.username || user.email)) || '',
  email: (user && user.email) || '',
  jobTitle: (user && user.company) || '',
  location: (user && user.country) || '',
});

const isObjectId = (value) => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

const Account = ({
  user,
  token,
  refreshToken,
  setSession: setSessionAction,
}) => {
  const currentUserId = (user && user.id) || null;
  const [profile, setProfile] = useState(() => ({
    ...EMPTY_PROFILE,
    ...mapUserToProfile(user),
  }));
  const [alert, setAlert] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [queuedSubscriptions, setQueuedSubscriptions] = useState([]);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [isPlanSubmitting, setIsPlanSubmitting] = useState(false);
  const [isCancellingRequest, setIsCancellingRequest] = useState(false);
  const [preferredPlanId, setPreferredPlanId] = useState(null);
  const defaultPlans = useMemo(
    () =>
      PLAN_CATALOG.map((plan) => ({
        id: plan.id,
        name: plan.name,
        price: formatMonthlyPrice(plan.priceAmount, plan.billingPeriod, plan.billingUnit),
        priceAmount: plan.priceAmount,
        billingPeriod: plan.billingPeriod,
        billingUnit: plan.billingUnit,
        features: plan.features,
        productId: plan.id,
      })),
    [],
  );

  const mapPublishedPlans = useMemo(
    () =>
      (plans) => {
        if (!Array.isArray(plans)) {
          return [];
        }
        return plans
          .filter((plan) => plan && typeof plan === 'object')
          .map((plan) => ({
            id: plan.planId || plan.productId,
            name: plan.name || plan.description || 'Plan',
            price: formatMonthlyPrice(
              plan.priceAmount != null
                ? plan.priceAmount
                : Number(String(plan.price).replace(/[^0-9.]/g, '')) || 0,
              plan.billingPeriod != null ? plan.billingPeriod : plan.period,
              plan.billingUnit || plan.periodUnit || 'month',
            ),
            priceAmount:
              plan.priceAmount != null
                ? Number(plan.priceAmount)
                : Number(String(plan.price).replace(/[^0-9.]/g, '')) || 0,
            billingPeriod: plan.billingPeriod != null ? plan.billingPeriod : plan.period || 1,
            billingUnit: plan.billingUnit || plan.periodUnit || 'month',
            features: Array.isArray(plan.features) ? plan.features : [],
            productId: plan.productId || plan.planId,
            description: plan.description || plan.name,
          }));
      },
    [],
  );

  const [availablePlans, setAvailablePlans] = useState(() => {
    const initial = mapPublishedPlans(getPublishedPlans());
    return initial.length ? initial : defaultPlans;
  });

  const showAlert = useCallback((message, tone = 'success') => {
    setAlert({ message, tone });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  }, []);

  const loadRequests = useCallback(async () => {
    if (!currentUserId) {
      setRequests([]);
      return;
    }
    try {
      const response = await accountApi.fetchSubscriptionRequests();
      const payload = response && response.data ? response.data : {};
      setRequests(Array.isArray(payload.data) ? payload.data : []);
    } catch (error) {
      setRequests([]);
    }
  }, [currentUserId]);

  const loadAccountSummary = useCallback(async () => {
    if (!currentUserId) {
      setSubscription(null);
      setQueuedSubscriptions([]);
      setIsSubscriptionLoading(false);
      return;
    }

    setIsSubscriptionLoading(true);
    try {
      const response = await accountApi.fetchProfile();
      const payload = response && response.data ? response.data : {};
      if (payload.user) {
        setProfile((prev) => ({
          ...prev,
          ...mapUserToProfile(payload.user),
        }));
      }
      setSubscription(payload.subscription ? mapSubscriptionFromApi(payload.subscription) : null);
      setQueuedSubscriptions(mapSubscriptionsFromApi(payload.queuedSubscriptions));
    } catch (error) {
      setSubscription(null);
      setQueuedSubscriptions([]);
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    setProfile((prev) => ({
      ...prev,
      ...mapUserToProfile(user),
    }));
  }, [user]);

  useEffect(() => {
    const unsubscribe = subscribeToPublishedPlans((plans) => {
      const normalized = mapPublishedPlans(plans);
      if (normalized.length) {
        setAvailablePlans(normalized);
      } else {
        setAvailablePlans(defaultPlans);
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [defaultPlans, mapPublishedPlans]);

  useEffect(() => {
    setPreferredPlanId(consumeSelectedPlanId());
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    loadAccountSummary();
  }, [loadAccountSummary]);

  const handleProfileSave = async (nextProfile) => {
    try {
      const response = await accountApi.updateProfile({
        name: nextProfile.displayName,
        company: nextProfile.jobTitle,
        country: nextProfile.location,
      });
      const updatedUser = response && response.data && response.data.user
        ? response.data.user
        : null;
      if (updatedUser) {
        setProfile((prev) => ({
          ...prev,
          ...mapUserToProfile(updatedUser),
          bio: nextProfile.bio,
        }));
        setSessionAction(updatedUser, token, refreshToken);
      } else {
        setProfile(nextProfile);
      }
      showAlert('Profile updated successfully.');
    } catch (error) {
      showAlert('Unable to update profile. Please try again.', 'danger');
      throw error;
    }
  };

  const handlePlanChange = async (planId) => {
    if (!currentUserId) {
      showAlert('Unable to submit request. User session missing.', 'danger');
      return;
    }
    const plan = availablePlans.find((item) => item.id === planId) || null;

    if (!plan) {
      showAlert('Choose a valid plan before submitting.', 'warning');
      return;
    }

    setIsPlanSubmitting(true);
    try {
      const resolvedProductId = isObjectId(plan.productId)
        ? plan.productId
        : isObjectId(plan.id)
        ? plan.id
        : null;
      const response = await accountApi.submitSubscriptionRequest({
        planId: plan.id,
        planName: plan.name,
        productId: resolvedProductId,
        planPriceDisplay: plan.price,
        planPriceAmount: plan.priceAmount,
        planBillingPeriod: plan.billingPeriod,
        planBillingUnit: plan.billingUnit,
        planFeatures: plan.features,
      });
      if (!response) {
        throw new Error('SUBSCRIPTION_REQUEST_FAILED');
      }
      await loadRequests();
      await loadAccountSummary();
      showAlert('Plan change request sent to the admin for approval.', 'info');
    } catch (error) {
      const apiMessage =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : null;
      showAlert(apiMessage || 'Unable to submit plan change. Please try again.', 'danger');
    } finally {
      setIsPlanSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!requestId) {
      return;
    }
    if (!currentUserId) {
      showAlert('Unable to cancel the request. User session missing.', 'danger');
      return;
    }
    setIsCancellingRequest(true);
    try {
      const response = await accountApi.cancelSubscriptionRequest(requestId);
      if (!response) {
        throw new Error('SUBSCRIPTION_CANCEL_FAILED');
      }
      await loadRequests();
      await loadAccountSummary();
      showAlert('Credit plan request cancelled.', 'info');
    } catch (error) {
      showAlert('Unable to cancel the credit plan request.', 'danger');
    } finally {
      setIsCancellingRequest(false);
    }
  };

  const pendingRequest = useMemo(
    () => requests.find((request) => request.status === 'pending') || null,
    [requests],
  );

  const requestHistory = useMemo(
    () =>
      requests
        .filter((request) => request.status !== 'pending')
        .sort(
          (a, b) =>
            new Date(b.resolvedAt || b.submittedAt).getTime() -
            new Date(a.resolvedAt || a.submittedAt).getTime(),
        ),
    [requests],
  );

  return (
    <section className="account-page">
      <header className="page-header">
        <h1>
          <span className="eyebrow">Account</span>
          <span className="headline">Manage your profile and credit plan</span>
        </h1>
      </header>

      {alert && <div className={`account-alert account-alert--${alert.tone}`}>{alert.message}</div>}

      <div className="account-grid">
        <ProfileSettingsForm
          profile={profile}
          onSave={handleProfileSave}
        />

        <SubscriptionPanel
          subscription={subscription}
          queuedSubscriptions={queuedSubscriptions}
          plans={availablePlans}
          preferredPlanId={preferredPlanId}
          pendingRequest={pendingRequest}
          requestHistory={requestHistory}
          onPlanSubmit={handlePlanChange}
          onCancelRequest={handleCancelRequest}
          isSubmitting={isPlanSubmitting}
          isCancelling={isCancellingRequest}
          isLoading={isSubscriptionLoading}
        />
      </div>
    </section>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  token: state.auth.token,
  refreshToken: state.auth.refreshToken,
});

export default connect(mapStateToProps, { setSession })(Account);
